import type { ComponentType } from "react";
import { Link, Outlet, useMatches, useNavigate } from "@tanstack/react-router";
import { CalendarCheck, Globe, ListChecks, MagnifyingGlassMinus, PlugsConnected, Storefront, Table, Target, UsersFour, UsersThree } from "@phosphor-icons/react";
import { AppShell, AuthGate, SignInPage, coreUrl, homeLinkClass, navLinkActiveClass, navLinkClass, useAuthUser, type AuthUser } from "@ghezaa/shell";
import type { Market, MarketsAnswer } from "@/api/client";
import { MarketProvider, MarketSwitch, useMarket } from "@/lib/market";

const APP_NAME = "Sales";

type NavItem = {
  to: "/$market/today" | "/$market/team" | "/$market/targets" | "/$market/customers" | "/$market/reports" | "/$market/data-gaps" | "/$market/people" | "/$market/business-central" | "/markets" | "/log";
  label: string;
  icon: ComponentType<{ size?: number }>;
  /** Whether the person may open it, in the market on screen (null when they have none). */
  shows: (market: Market | null, answer: MarketsAnswer | undefined) => boolean;
};

type NavGroup = { label: string; items: NavItem[] };

// A group shows only when the person may open one of its items.
const navigation: NavGroup[] = [
  {
    label: "Sales",
    items: [
      { to: "/$market/today", label: "Today", icon: CalendarCheck, shows: (m) => Boolean(m?.can.own || m?.can.team) },
      { to: "/$market/team", label: "Team", icon: UsersFour, shows: (m) => Boolean(m?.can.team) },
      { to: "/$market/targets", label: "Targets", icon: Target, shows: (m) => Boolean(m?.can.team) },
      { to: "/$market/customers", label: "Customers", icon: Storefront, shows: (m) => Boolean(m?.can.own || m?.can.team) },
      { to: "/$market/reports", label: "Reports", icon: Table, shows: (m) => Boolean(m?.can.reports) },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/$market/data-gaps", label: "Data gaps", icon: MagnifyingGlassMinus, shows: (m) => Boolean(m?.can.team) },
      { to: "/$market/people", label: "People", icon: UsersThree, shows: (m) => Boolean(m?.can.settings) },
      { to: "/$market/business-central", label: "Business Central", icon: PlugsConnected, shows: (m) => Boolean(m?.can.erp) },
      { to: "/markets", label: "Markets", icon: Globe, shows: (m, a) => Boolean(a?.canSetUp || m?.can.settings) },
      { to: "/log", label: "Log", icon: ListChecks, shows: (_m, a) => Boolean(a?.canSeeLog) },
    ],
  },
];

const marketPages: string[] = navigation.flatMap((g) => g.items.map((i) => i.to)).filter((to) => to.startsWith("/$market"));

/** The section a route belongs to: the nav item with the longest matching path (a customer's page is under Customers). */
function sectionOf(fullPath: string) {
  const path = fullPath.replace(/\/+$/, "");
  return marketPages.filter((to) => path === to || path.startsWith(`${to}/`)).sort((a, b) => b.length - a.length)[0] ?? "/$market/today";
}

/** Switching market changes the address; on a record's page (a customer belongs to one market) it goes back to the list. */
function SidebarMarket() {
  const here = useMatches({ select: (matches) => matches[matches.length - 1] });
  const navigate = useNavigate();
  const path = here?.fullPath ?? "";
  const onRecord = Object.keys(here?.params ?? {}).some((key) => key !== "market");
  const to = !path.startsWith("/$market") || onRecord ? sectionOf(path) : path;
  return <MarketSwitch onSwitch={(market) => void navigate({ to: to as never, params: { market } as never, search: {} as never })} />;
}

function SalesNav() {
  const { home, answer } = useMarket();
  return (
    <div className="flex flex-col gap-4">
      {navigation.map((group) => {
        const items = group.items.filter((item) => item.shows(home, answer));
        if (items.length === 0) return null;
        return (
          <div key={group.label} className="flex flex-col gap-px" role="group" aria-label={group.label}>
            <p className="px-2 pb-1 text-2xs font-medium uppercase tracking-wider text-ink-3">{group.label}</p>
            {items.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} params={{ market: home?.code ?? "" } as never} className={navLinkClass} activeProps={{ className: navLinkActiveClass }}>
                <Icon size={16} />
                {label}
              </Link>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** Entra sign-in, permissions from Core, and the market every page shows. */
export function Shell() {
  const user = useAuthUser();
  return (
    <AuthGate
      query={user}
      appName={APP_NAME}
      signIn={<SignInPage appName={APP_NAME} headline="Your customers, from Business Central." description="Sign in with your Ghezaa Microsoft account." />}
    >
      {(me) => (
        <MarketProvider>
          <Frame me={me} />
        </MarketProvider>
      )}
    </AuthGate>
  );
}

function Frame({ me }: { me: AuthUser }) {
  const { markets } = useMarket();
  return (
    <AppShell
      appName={APP_NAME}
      switchRoleHref={`${coreUrl(import.meta.env.VITE_CORE_URL, import.meta.env.DEV)}/view-as`}
      home={(mark) => (
        <Link to="/" className={homeLinkClass} aria-label="Sales home">
          {mark}
        </Link>
      )}
      user={{ name: me.name, email: me.email }}
      scope={markets.length > 1 ? <SidebarMarket /> : undefined}
      nav={<SalesNav />}
    >
      <Outlet />
    </AppShell>
  );
}
